import React from 'react';
import classnames from 'classnames';
import { Icon, IconTypes } from '@eika/icon';
import { StandardButton, LinkButton, LinkButtonProps, StandardButtonProps } from '@eika/button';
import { RichIcon, RichIconTypes } from '@eika/rich-icon';

import '@eika/card-style/src/MarketCard/index.scss';

type BasicDivElementProps = React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLDivElement>, HTMLDivElement>;

export enum ImagePosition {
    CENTER = 'image-position__center',
    TOP = 'image-position__top',
    RIGHT = 'image-position__right',
    BOTTOM = 'image-position__bottom',
    LEFT = 'image-position__left',
}

interface MarketCardImageProps {
    src: string;
    alt?: string;
}

interface IllustrationHeaderProps {
    image: MarketCardImageProps;
}

interface ImageHeaderProps extends IllustrationHeaderProps {
    imagePosition?: ImagePosition;
}

interface IconHeaderProps {
    icon: IconTypes;
}

interface RichIconHeaderProps {
    icon: RichIconTypes;
    imagePosition?: ImagePosition;
}

export interface MarketCardProps extends BasicDivElementProps {
    className?: string;
    header?: JSX.Element;
    title?: string;
    primaryButton?: JSX.Element;
    secondaryButton?: JSX.Element;
    alignTextLeft?: boolean;
}

interface MarketCardComposition {
    ImageHeader: React.FC<ImageHeaderProps>;
    IllustrationHeader: React.FC<IllustrationHeaderProps>;
    IconHeader: React.FC<IconHeaderProps>;
    PrimaryButton: React.FC<StandardButtonProps>;
    SecondaryButton: React.FC<LinkButtonProps>;
    RichIconHeader: React.FC<RichIconHeaderProps>;
}

const ImageHeader: React.FC<ImageHeaderProps> = ({ image, imagePosition }: ImageHeaderProps) => {
    return (
        <div className={classnames('sgw-market-card__head sgw-market-card__head__image', imagePosition)}>
            <picture className="sgw-market-card__head-wrapper">
                <img className="sgw-market-card__image" src={image.src} alt={image.alt} />
            </picture>
        </div>
    );
};

const IllustrationHeader: React.FC<IllustrationHeaderProps> = ({ image }) => {
    return (
        <div className="sgw-market-card__head sgw-market-card__head__illustration">
            <picture className="sgw-market-card__head-wrapper">
                <img className="sgw-market-card__illustration" src={image.src} alt={image.alt} />
            </picture>
        </div>
    );
};

const IconHeader: React.FC<IconHeaderProps> = ({ icon }) => {
    return (
        <div className="sgw-market-card__head sgw-market-card__head__icon">
            <div className="sgw-market-card__icon-container">
                <Icon className="sgw-market-card__icon-container__icon" type={icon} />
            </div>
        </div>
    );
};

const RichIconHeader: React.FC<RichIconHeaderProps> = ({ icon, imagePosition }) => {
    return (
        <div className={classnames('sgw-market-card__head sgw-market-card__head__illustration', imagePosition)}>
            <div className="sgw-market-card__head-wrapper">
                <RichIcon type={icon} />
            </div>
        </div>
    );
};

const MarketCard: React.FC<MarketCardProps> & MarketCardComposition = ({
    className,
    header,
    title,
    children,
    primaryButton,
    secondaryButton,
    alignTextLeft = false,
    ...rest
}) => {
    return (
        // eslint-disable-next-line
        <div
            className={classnames('sgw-market-card', className, {
                'has-header': header,
                'align-text-left left-adjusted': alignTextLeft,
            })}
            {...rest}
        >
            {header}
            <div className="sgw-market-card__content">
                {title && <h3 className="typography-h3 sgw-market-card__title">{title}</h3>}
                {children && <div className="sgw-market-card__text">{children}</div>}
            </div>
            <div className="sgw-market-card__footer">
                {primaryButton && <div className="sgw-market-card__button">{primaryButton}</div>}
                {secondaryButton && <div className="sgw-market-card__button">{secondaryButton}</div>}
            </div>
        </div>
    );
};

MarketCard.ImageHeader = ImageHeader;
MarketCard.IllustrationHeader = IllustrationHeader;
MarketCard.IconHeader = IconHeader;
MarketCard.PrimaryButton = StandardButton;
MarketCard.SecondaryButton = LinkButton;
MarketCard.RichIconHeader = RichIconHeader;

export default MarketCard;
